/**
 * AR-420 · The checkout's calls to `/api/checkout`. The browser session cookie
 * ties them to one checkout; nothing here carries card data — the card goes
 * from the Payment Element straight to Stripe.
 */

export interface CatalogueResponse {
  plans: Array<{ planId: number; key: string; name: string; monthlyCents: number; setupFeeCents: number; rank: number }>;
  accelerator: { priceCents: number } | null;
  publishableKey: string | null;
}

export type CouponResponse =
  | { valid: true; code: string; label: string; amountOffCents: number | null; percentOff: number | null; duration: string }
  | { valid: false; code: string; reason: string };

export interface PaymentResponse {
  clientSecret: string | null;
  amountDueCents: number;
  subscriptionId: string;
}

export interface StatusResponse {
  checkoutSessionId: number;
  status: 'started' | 'contact' | 'payment_pending' | 'paid' | 'abandoned';
  step: number;
  plan: CatalogueResponse['plans'][number] | null;
  email: string;
  firstName: string;
  lastName: string;
  contact: Record<string, unknown> | null;
  acceleratorSelected: boolean;
  couponCode: string | null;
  lastError: string | null;
  paidAt: string | null;
}

export class CheckoutApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

const call = async <T>(method: string, path: string, body?: unknown): Promise<T> => {
  let response: Response;
  try {
    response = await fetch(`/api/checkout${path}`, {
      method,
      credentials: 'include',
      headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new CheckoutApiError(0, 'We couldn’t reach WorldVia. Check your connection and try again.');
  }
  if (response.status === 204) return undefined as T;
  const json = await response.json().catch(() => null);
  if (!response.ok) {
    const message =
      response.status === 429
        ? 'Too many attempts from this connection. Wait a few minutes and try again.'
        : typeof json?.message === 'string'
          ? json.message
          : 'Something went wrong. Please try again.';
    throw new CheckoutApiError(response.status, message);
  }
  return json as T;
};

export const checkoutApi = {
  catalogue: () => call<CatalogueResponse>('GET', '/catalogue'),
  /** `password` goes to the server only to be passed straight to Auth0 (AR-420); it is never stored. */
  start: (input: { planKey: string; email: string; firstName: string; lastName: string; password: string }) =>
    call<{ status: 'started' | 'exists' }>('POST', '/start', input),
  status: () => call<StatusResponse>('GET', '/status'),
  contact: (contact: unknown) => call<void>('POST', '/contact', contact),
  accelerator: (selected: boolean) => call<void>('POST', '/accelerator', { selected }),
  applyCoupon: (code: string) => call<CouponResponse>('POST', '/coupon', { code }),
  removeCoupon: () => call<void>('DELETE', '/coupon'),
  payment: () => call<PaymentResponse>('POST', '/payment'),
  agreement: (termsAccepted: boolean, marketingOptIn: boolean) =>
    call<void>('POST', '/agreement', { termsAccepted, marketingOptIn }),
};
